import { Command, Store, addCandidateNote } from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCreateDirectConversation } from 'chat/conversations/queries/create_direct'
import { sendDirectMessage } from 'chat/conversations/utils'
import { ShiftScreenProps } from 'home/stack/types'
import React, { useCallback, useLayoutEffect } from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { onCallSMS, openMap } from 'shared/utils'
import { useShift } from 'shifts/queries/detail'
import { usePerformShiftAction } from 'shifts/queries/perform_action'
import { useShiftActions } from 'shifts/shift_actions_context'
import { useUser } from 'users/queries/detail'
import { ShiftView } from './views'

export function ShiftScreen({ navigation, route }: ShiftScreenProps) {
  const shiftQuery = useShift(route.params)
  const { setActionCommand } = useShiftActions()
  const { handleNavigation } = useRoutingContext()
  const createDirectConversationMutation = useCreateDirectConversation()
  useLayoutEffect(() => {
    showHeaderTitle(navigation, shiftQuery.data?.title ?? t('shift'))
  }, [navigation, shiftQuery.data?.title])

  const shiftMutation = usePerformShiftAction(route.params)
  const { data: user } = useUser()

  const onButtonPress = useCallback(
    async (command: Command, note?: string) => {
      if (command.type === 'action') {
        if (note) {
          const commandWithNote: Command = {
            ...command,
            action: addCandidateNote(command.action, note),
          }
          setActionCommand(commandWithNote)
        } else {
          setActionCommand(command)
        }
        if (command.name === 'change_incentive_amount') {
          if (shiftQuery.data) {
            handleNavigation({
              routeName: 'edit_critical_shift_bonus',
              routeParams: {
                shiftData: shiftQuery.data,
              },
            })
          }
        } else if (command.name === 'edit_shift') {
          handleNavigation({
            routeName: 'edit_shift',
            routeParams: route.params,
          })
        } else if (command.name === 'invite_user') {
          handleNavigation({
            routeName: 'add_mobile_number',
            routeParams: {
              action: command.action,
              uri: command.action.uri,
            },
          })
        } else if (command.name === 'swap_shift') {
          if (shiftQuery.data?.shift?.id) {
            handleNavigation({
              routeName: 'user_list',
              routeParams: {
                type: 'store',
                filter: 'swap_eligible',
                filter_shift_id: shiftQuery.data.shift.id,
              },
            })
          }
        } else if (command.name === 'crew_swap_shift') {
          if (shiftQuery.data?.shift?.id) {
            handleNavigation({
              routeName: 'user_list',
              routeParams: {
                type: 'store',
                filter: 'swap_eligible',
                filter_shift_id: shiftQuery.data.shift.id,
                isCrewInitiatedSwap: true,
              },
            })
          }
        } else if (command.name === 'approve_crew_swap_shift') {
          try {
            const shiftData = await shiftMutation.mutateAsync(
              addCandidateNote(command.action, note)
            )
            if (shiftData) {
              handleNavigation({
                routeName: 'swap_cover_shift',
                routeParams: { shiftData },
              })
            }
          } catch (err: any) {
            errorAlert(err, t('shiftActionErrorTitle'))
          }
        } else if (command.name === 'cover_shift') {
          if (shiftQuery.data?.shift?.id) {
            handleNavigation({
              routeName: 'user_list',
              routeParams: {
                type: 'store',
                filter: 'cover_eligible',
                filter_shift_id: shiftQuery.data.shift.id,
                store: shiftQuery.data?.shift?.store,
              },
            })
          }
        } else if (command.name === 'shift_rating') {
          handleNavigation({
            routeName: 'schedule',
            routeParams: {
              rateCrew: true,
              uri: command.action.uri,
            },
          })
        } else if (command.name === 'message_manager') {
          const body: any = command.action.params?.body
          handleNavigation({
            routeName: 'compose',
            routeParams: {
              type: 'messageManagers',
              storeId: shiftQuery.data?.shift?.store.id,
              message: body,
            },
          })
        } else if (command.name === 'cancel_shift') {
          const cancelTypes = command.action?.params?.sub_type.split('|')
          if (cancelTypes.length > 1) {
            handleNavigation({ routeName: 'cancel_shift' })
          } else {
            try {
              const shiftData = await shiftMutation.mutateAsync(
                addCandidateNote(command.action, note)
              )

              if (shiftData) {
                handleNavigation({
                  routeName: 'confirm_shift_action',
                  routeParams: {
                    shiftData: shiftData,
                  },
                })
              }
            } catch (err: any) {
              errorAlert(err, t('cancelShiftErrorTitle'))
            }
          }
        } else if (command.name === 'dm') {
          const recipientId = command.action.params?.recipient_id
          await sendDirectMessage(
            navigation,
            createDirectConversationMutation,
            recipientId
          )
        } else {
          try {
            const shiftData = await shiftMutation.mutateAsync(
              addCandidateNote(command.action, note)
            )
            if (
              shiftData &&
              shiftData.title !== 'Status' &&
              shiftData.title !== 'Shift detail'
            ) {
              handleNavigation({
                routeName: 'confirm_shift_action',
                routeParams: {
                  shiftData: shiftData,
                },
              })
            }
          } catch (err: any) {
            errorAlert(err, t('shiftActionErrorTitle'))
          }
        }
      } else if (command.type === 'call' || command.type === 'sms') {
        await onCallSMS(command)
      }
    },
    [
      createDirectConversationMutation,
      handleNavigation,
      navigation,
      route.params,
      setActionCommand,
      shiftMutation,
      shiftQuery.data,
    ]
  )
  const onStorePress = (store: Store) => {
    const address = `${store.address},${store.city},${store.zipCode}`
    openMap(address, store.name).catch(e => console.log(e))
  }

  return (
    <StatusBoundary
      isLoading={shiftQuery.isLoading}
      error={shiftQuery.error}
    >
      <ShiftView
        shiftData={shiftQuery.data}
        onStorePress={onStorePress}
        onButtonPress={onButtonPress}
        role={user?.role ?? 'helper'}
      />
    </StatusBoundary>
  )
}
